import type { Component } from 'vue';
import { Module } from 'vuex';

export interface Tab {
    index: number;
    props: object;
    name: string;
    component: Component;
    disabled: boolean;
    [key: string]: unknown;
}

export type ModalState = Record<number, ModalTabState>;

interface ModalTabState {
    tabs: Tab[];
    active_tab: number;
}

const state: ModalState = {
    0: {
        tabs: [],
        active_tab: 0,
    },
};

const modalTabModule: Module<ModalState, unknown> = {
    namespaced: true,
    state,
    mutations: {
        setHeaders(
            state: ModalState,
            { headers, modalId = 0 }: { headers: Tab[]; modalId?: number },
        ) {
            if (state[modalId] === undefined) {
                state[modalId] = { tabs: [], active_tab: 0 };
            }
            state[modalId].tabs = headers;
        },
        setActiveTab(
            state: ModalState,
            { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number },
        ) {
            state[modalId].active_tab = tabIndex;
        },
        clearTabs(state: ModalState, modalId: number = 0) {
            if (state[modalId]) {
                state[modalId].tabs = [];
            }
        },
        hideTab(
            state: ModalState,
            { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number },
        ) {
            state[modalId].tabs[tabIndex].disabled = true;
        },
        showTab(
            state: ModalState,
            { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number },
        ) {
            state[modalId].tabs[tabIndex].disabled = false;
        },
    },
    actions: {
        setTabs({ commit }, { headers, modalId = 0 }: { headers: Tab[]; modalId?: number }) {
            commit('setHeaders', { headers, modalId });
        },
        changeActiveTab(
            { commit },
            { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number },
        ) {
            commit('setActiveTab', { tabIndex, modalId });
        },
        clearTabs({ commit }, modalId: number = 0) {
            commit('clearTabs', modalId);
        },
        hideTab({ commit }, { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number }) {
            commit('hideTab', { tabIndex, modalId });
        },
        showTab({ commit }, { tabIndex, modalId = 0 }: { tabIndex: number; modalId?: number }) {
            commit('showTab', { tabIndex, modalId });
        },
    },
    getters: {
        getTabs:
            (state: ModalState) =>
            (modalId: number = 0) => {
                if (state[modalId] === undefined) {
                    return [];
                }
                return state[modalId].tabs || [];
            },
        activeTab:
            (state: ModalState) =>
            (modalId: number = 0) => {
                if (state[modalId] === undefined) {
                    return 0;
                }
                return state[modalId].active_tab || 0;
            },
        nextVisibleTab:
            (state: ModalState) =>
            (modalId: number = 0, fromIndex: number = 0) => {
                const tabs = state[modalId]?.tabs ?? [];
                for (let i = fromIndex + 1; i < tabs.length; i += 1) {
                    if (!tabs[i].disabled) return i;
                }
                return fromIndex;
            },
        previousVisibleTab:
            (state: ModalState) =>
            (modalId: number = 0, fromIndex: number = 0) => {
                const tabs = state[modalId]?.tabs ?? [];
                for (let i = fromIndex - 1; i >= 0; i -= 1) {
                    if (!tabs[i].disabled) return i;
                }
                return fromIndex;
            },
        getLastModalId: (state: ModalState) => {
            const keys = Object.keys(state).map(Number);
            if (!keys.length) {
                return 0;
            }
            return keys[keys.length - 1];
        },
    },
};

export default modalTabModule;
